import { useMemo, useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Pill,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { money } from '../lib/format';
import type { CatalogItem, Merchant, Order, Paged } from '../lib/types';

interface CartLine {
  item: CatalogItem;
  quantity: number;
  billingMethod: string;
}

export function Equipment() {
  const { user } = useAuth();
  const catalog = useApi<CatalogItem[]>('/catalog?kind=equipment');
  const merchants = useApi<Paged<Merchant>>('/merchants?limit=100');

  const [category, setCategory] = useState('all');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [cart, setCart] = useState<Record<string, CartLine>>({});
  const [merchantId, setMerchantId] = useState('');
  const [billedTo, setBilledTo] = useState<'node' | 'agent' | 'merchant'>('node');
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categories = useMemo(() => {
    const set = new Set((catalog.data ?? []).map((c) => c.category));
    return ['all', ...[...set].sort()];
  }, [catalog.data]);

  const visible = (catalog.data ?? []).filter(
    (c) =>
      (category === 'all' || c.category === category) &&
      (!inStockOnly || c.availability === 'in_stock'),
  );

  const lines = Object.values(cart);
  const deviceCount = lines.reduce((s, l) => s + l.quantity, 0);
  const subtotal = lines.reduce(
    (s, l) => s + (l.billingMethod === 'placement' ? 0 : l.item.agentCostCents) * l.quantity,
    0,
  );

  function add(item: CatalogItem, billingMethod: string) {
    setCart((c) => {
      const existing = c[item.id];
      return {
        ...c,
        [item.id]: {
          item,
          billingMethod,
          quantity: (existing?.quantity ?? 0) + 1,
        },
      };
    });
    setPlaced(null);
  }

  function setQuantity(id: string, quantity: number) {
    setCart((c) => {
      if (quantity <= 0) {
        const { [id]: _drop, ...rest } = c;
        return rest;
      }
      return { ...c, [id]: { ...c[id], quantity } };
    });
  }

  async function placeOrder() {
    setPlacing(true);
    setError(null);
    try {
      const merchant = merchants.data?.items.find((m) => m.id === merchantId);
      const order = await api.post<Order>('/orders', {
        kind: 'equipment',
        lines: lines.map((l) => ({
          catalogId: l.item.id,
          quantity: l.quantity,
          billingMethod: l.billingMethod,
        })),
        shipTo: merchant?.address ?? {
          street: '[STREET]',
          city: '[CITY]',
          state: 'NY',
          zip: '[ZIP]',
        },
        billedTo,
        merchantId: merchant?.id,
        merchantName: merchant?.dba,
      });
      setPlaced(order);
      setCart({});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That order did not go through.');
    } finally {
      setPlacing(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Ordering', 'Equipment']}
        title="Equipment catalog"
        actions={<Button>Return or swap a device</Button>}
      />

      <ScopeBar>
        You can place devices for anyone in your downline. Purchases over{' '}
        <strong>$1,500</strong> need {user?.parentNodeName ?? 'parent office'} sign-off.
      </ScopeBar>

      <PageBody>
        <div className="split-2">
          <div className="stack">
            <Card>
              <div className="toolbar">
                <div className="segmented" role="group" aria-label="Category">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`segmented__btn ${category === c ? 'is-active' : ''}`}
                      aria-pressed={category === c}
                      onClick={() => setCategory(c)}
                    >
                      {c === 'all' ? 'All devices' : c}
                    </button>
                  ))}
                </div>
                <span className="spacer" />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                  />
                  In stock only
                </label>
              </div>

              {catalog.loading && <Loading />}
              {catalog.error && <ErrorState error={catalog.error} onRetry={catalog.reload} />}

              {catalog.data && visible.length === 0 && (
                <Empty title="Nothing in that category" />
              )}

              {visible.length > 0 && (
                <div className="catalog">
                  {visible.map((item) => (
                    <ProductCard key={item.id} item={item} onAdd={add} />
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card>
              <CardHeader title="This order" count={deviceCount || undefined} />

              <div className="stack" style={{ gap: 12 }}>
                <div className="field">
                  <label className="field__label">Ship to merchant</label>
                  <select
                    className="select"
                    value={merchantId}
                    onChange={(e) => setMerchantId(e.target.value)}
                  >
                    <option value="">Choose a merchant</option>
                    {(merchants.data?.items ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.dba} — MID {m.mid}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label className="field__label">Billed to</label>
                  <select
                    className="select"
                    value={billedTo}
                    onChange={(e) => setBilledTo(e.target.value as typeof billedTo)}
                  >
                    <option value="node">{user?.nodeName} — my office</option>
                    <option value="agent">The writing agent</option>
                    <option value="merchant">Merchant, on first statement</option>
                  </select>
                </div>
              </div>

              {lines.length === 0 ? (
                <Empty
                  title="Nothing added yet"
                  detail="Pick a device from the catalog to start an order."
                />
              ) : (
                <>
                  <div style={{ marginTop: 14 }}>
                    {lines.map((l) => (
                      <div className="cartline" key={l.item.id}>
                        <div className="cartline__text">
                          <div className="cartline__name">{l.item.name}</div>
                          <div className="cartline__sub">
                            {l.billingMethod === 'placement'
                              ? 'Placement · no charge to merchant'
                              : `${l.billingMethod} · ${money(l.item.agentCostCents)} each`}
                          </div>
                        </div>
                        <Stepper
                          value={l.quantity}
                          onChange={(q) => setQuantity(l.item.id, q)}
                          label={l.item.name}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="totals">
                    <div className="totals__row">
                      <span>Devices</span>
                      <span>{deviceCount}</span>
                    </div>
                    <div className="totals__row">
                      <span>Charged to your office</span>
                      <span>{money(subtotal)}</span>
                    </div>
                    <div className="totals__row">
                      <span>Estimated ship</span>
                      <span>Next business day</span>
                    </div>
                    {subtotal > 150_000 && (
                      <div className="notice notice--warn">
                        {Icon.alert({ size: 15 })}
                        <span>
                          Over the $1,500 limit — this will be parked awaiting sign-off
                          from {user?.parentNodeName ?? 'your parent office'}.
                        </span>
                      </div>
                    )}
                    <Button
                      variant="primary"
                      size="lg"
                      block
                      disabled={placing}
                      onClick={() => void placeOrder()}
                    >
                      {placing ? 'Placing…' : 'Place order'}
                    </Button>
                  </div>
                </>
              )}

              {error && (
                <div className="notice notice--danger" style={{ marginTop: 12 }}>
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}

              {placed && (
                <div
                  className={`notice notice--${placed.status === 'pending_approval' ? 'warn' : 'ok'}`}
                  style={{ marginTop: 12 }}
                  role="status"
                >
                  {Icon.check({ size: 15 })}
                  <span>
                    Order <strong className="mono">{placed.reference}</strong> placed —{' '}
                    {placed.status === 'pending_approval'
                      ? 'awaiting approval from your parent office.'
                      : 'packing now.'}
                  </span>
                </div>
              )}

              <p className="login__footnote">
                Placements stay free while the merchant processes. Devices unreturned
                after cancellation bill at replacement cost.
              </p>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function ProductCard({
  item,
  onAdd,
}: {
  item: CatalogItem;
  onAdd: (item: CatalogItem, billingMethod: string) => void;
}) {
  const [method, setMethod] = useState(item.billingMethods[0] ?? 'purchase');

  return (
    <article className="product">
      <div className="product__art">{Icon.equipment({ size: 34 })}</div>
      <div className="product__body">
        <div className="product__head">
          <h3 className="product__name">{item.name}</h3>
          <Pill tone="neutral">{item.category}</Pill>
          <span className="spacer" />
          <Pill tone={item.availability === 'in_stock' ? 'ok' : 'warn'}>
            {item.availability === 'in_stock'
              ? 'In stock'
              : item.availability === 'no_hardware'
                ? 'No hardware'
                : (item.leadTimeLabel ?? 'Lead time')}
          </Pill>
        </div>
        <p className="product__desc">{item.description}</p>
        <div className="product__price">
          {item.monthlyCents ? (
            <>
              <strong>{money(item.monthlyCents)}</strong> per month
            </>
          ) : (
            <>
              <strong>{money(item.agentCostCents)}</strong> agent cost
            </>
          )}
        </div>
        <div className="product__actions">
          <select
            className="select"
            aria-label={`Billing method for ${item.name}`}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {item.billingMethods.map((m) => (
              <option key={m} value={m}>
                {m === 'placement' ? 'Placement' : m === 'rental' ? 'Rental' : 'Purchase'}
              </option>
            ))}
          </select>
          <Button variant="primary" size="sm" onClick={() => onAdd(item, method)}>
            Add
          </Button>
        </div>
      </div>
    </article>
  );
}

export function Stepper({
  value,
  onChange,
  label,
  min = 0,
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
  min?: number;
}) {
  return (
    <div className="stepper">
      <button
        type="button"
        aria-label={`Fewer ${label}`}
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
      >
        {Icon.minus({ size: 13 })}
      </button>
      <span className="stepper__value">{value}</span>
      <button type="button" aria-label={`More ${label}`} onClick={() => onChange(value + 1)}>
        {Icon.plus({ size: 13 })}
      </button>
    </div>
  );
}
